import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Quiz as QuizIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  PlayArrow as PlayArrowIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import {
  verifySession,
  studentLogout,
  getUserId,
  getUserName,
  getAssignedAssessments,
  getGlobalAssessments,
} from '../../utils/api';
import LevavLogo from '../../sources/Logo/LEVAV Logo.png';
import ChangeLanguage from '../sections/ChangeLanguage';

function StudentHome() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(1); // Changed to 1 for Global tab
  const [assignedAssessments, setAssignedAssessments] = useState([]);
  const [globalAssessments, setGlobalAssessments] = useState([]);
  const [userName, setUserName] = useState(getUserName() || `${t('StudentHome.user')} ${getUserId()}`);

  useEffect(() => {
    checkAuthAndFetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndFetchData = async () => {
    try {
      console.log('Before verify - userId:', getUserId(), 'userName:', getUserName());
      
      const isValid = await verifySession();
      if (!isValid) {
        navigate('/student-login');
        return;
      }
      
      // Update userName from localStorage after verification
      const userId = getUserId();
      const name = getUserName();
      console.log('After verify - userId:', userId, 'userName:', name);
      
      if (name) {
        setUserName(name);
      } else {
        // Fallback to userId if name is not available
        setUserName(`${t('StudentHome.user')} ${userId}`);
      }
      
      await fetchAssessments();
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/student-login');
    }
  };

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both assigned and global assessments
      const [assigned, global] = await Promise.all([
        getAssignedAssessments(),
        getGlobalAssessments(),
      ]);
      
      setAssignedAssessments(assigned || []);
      setGlobalAssessments(global || []);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setError(t('StudentHome.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await studentLogout();
    navigate('/student-login');
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleAssessmentClick = (assessment, isAssigned = false) => {
    const assessmentId = isAssigned ? assessment.assessmentId : assessment.id;
    const taskId = isAssigned ? assessment.taskId : null;
    navigate(`/student/assessment/${assessmentId}${taskId ? `?taskId=${taskId}` : ''}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderAssessmentCard = (assessment, isAssigned = false) => {
    const topic = isAssigned ? assessment.topic || t('StudentHome.untitledAssessment') : assessment.topic;
    const questionCount = assessment.questionCount || 0;
    const createdAt = isAssigned ? assessment.startDate : assessment.createdAt;
    
    return (
      <Grid item xs={12} sm={6} md={4} key={isAssigned ? assessment.taskId : assessment.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '2px solid',
            borderColor: '#e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 12px 24px rgba(126, 205, 163, 0.25)',
              borderColor: isAssigned ? '#7ECDA3' : '#A8E6CF',
              cursor: 'pointer',
            },
          }}
          onClick={() => handleAssessmentClick(assessment, isAssigned)}
        >
          {/* Card Header with Gradient */}
          <Box
            sx={{
              background: isAssigned 
                ? 'linear-gradient(135deg, #7ECDA3 0%, #5FB88A 100%)'
                : 'linear-gradient(135deg, #A8E6CF 0%, #7ECDA3 100%)',
              p: 2.5,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255,255,255,0.1)',
                opacity: 0,
                transition: 'opacity 0.3s',
              },
              '&:hover::after': {
                opacity: 1,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
              <Chip
                icon={isAssigned ? <AssignmentIcon sx={{ fontSize: 16 }} /> : <TrendingUpIcon sx={{ fontSize: 16 }} />}
                label={isAssigned ? t('StudentHome.assigned') : t('StudentHome.global')}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  backdropFilter: 'blur(10px)',
                }}
              />
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,255,255,0.25)',
                  width: 36,
                  height: 36,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <QuizIcon sx={{ color: 'white', fontSize: 20 }} />
              </Avatar>
            </Box>
            
            <Typography
              variant="h6"
              component="h3"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: '1.1rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                minHeight: '3.3em',
                lineHeight: 1.4,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {topic}
            </Typography>
          </Box>

          {/* Card Content */}
          <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Question Count */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QuizIcon sx={{ fontSize: 18, color: '#5FB88A' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {questionCount} {questionCount === 1 ? t('StudentHome.question') : t('StudentHome.questions')}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 0.5 }} />
              
              {/* Created/Assigned Date */}
              {createdAt && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon sx={{ fontSize: 18, color: '#7ECDA3' }} />
                  <Typography variant="body2" color="text.secondary">
                    {isAssigned ? t('StudentHome.assignedDate') : t('StudentHome.createdDate')}: {formatDate(createdAt)}
                  </Typography>
                </Box>
              )}
              
              {/* Due Date (for assigned assessments) */}
              {isAssigned && assessment.expiredDate && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    bgcolor: '#fff3e0',
                    p: 1,
                    borderRadius: 1.5,
                    border: '1px solid #ffe0b2',
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 18, color: '#f57c00' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                    {t('StudentHome.due')}: {formatDate(assessment.expiredDate)}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
          
          {/* Card Actions */}
          <CardActions sx={{ p: 2.5, pt: 0 }}>
            <Button
              variant="contained"
              fullWidth
              endIcon={<PlayArrowIcon />}
              sx={{
                background: 'linear-gradient(135deg, #7ECDA3 0%, #5FB88A 100%)',
                color: 'white',
                py: 1.2,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(126, 205, 163, 0.4)',
                transition: 'all 0.3s',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5FB88A 0%, #4A9D7A 100%)',
                  boxShadow: '0 6px 16px rgba(126, 205, 163, 0.5)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {t('StudentHome.startAssessment')}
            </Button>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t('StudentHome.loadingAssessments')}</Typography>
      </Container>
    );
  }

  const currentAssessments = selectedTab === 0 ? assignedAssessments : globalAssessments;

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        bgcolor: '#fafafa',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 3,
            p: 3,
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img 
              src={LevavLogo} 
              alt="LEVAV Logo" 
              style={{ height: '50px', width: 'auto' }}
            />
            <Box>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 700, 
                  background: 'linear-gradient(135deg, #5FB88A 0%, #4A9D7A 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5,
                }}
              >
                {t('StudentHome.welcome')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar 
                  sx={{ 
                    width: 24, 
                    height: 24, 
                    bgcolor: '#7ECDA3',
                    fontSize: '0.75rem',
                  }}
                >
                  {userName.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                  {userName}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ChangeLanguage buttonColor="#5FB88A" />
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                borderColor: '#7ECDA3',
                color: '#5FB88A',
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#5FB88A',
                  bgcolor: 'rgba(126, 205, 163, 0.08)',
                },
              }}
            >
              {t('StudentHome.logout')}
            </Button>
          </Box>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Main Content */}
        <Box 
          sx={{ 
            bgcolor: 'white', 
            borderRadius: 3, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
          }}
        >
          {/* Tabs */}
          <Box
            sx={{
              bgcolor: 'white',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  minHeight: 64,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: '#5FB88A',
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: 'linear-gradient(135deg, #7ECDA3 0%, #5FB88A 100%)',
                },
              }}
            >
              <Tab
                icon={<AssignmentIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label={`${t('StudentHome.assigned')} (${assignedAssessments.length})`}
              />
              <Tab
                icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label={`${t('StudentHome.global')} (${globalAssessments.length})`}
              />
            </Tabs>
          </Box>

          {/* Assessment Cards */}
          <Box sx={{ p: 4 }}>
            {currentAssessments.length === 0 ? (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(168, 230, 207, 0.15), rgba(126, 205, 163, 0.15))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <QuizIcon sx={{ fontSize: 60, color: '#7ECDA3', opacity: 0.5 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {t('StudentHome.noAssessmentsAvailable')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                  {selectedTab === 0
                    ? t('StudentHome.noAssignedMessage')
                    : t('StudentHome.noGlobalMessage')}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {currentAssessments.map((assessment) =>
                  renderAssessmentCard(assessment, selectedTab === 0)
                )}
              </Grid>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default StudentHome;
